/**
 * Author: Claude Code
 * Version: v3.0.103
 * Modified: 2025-09-09 Beijing Time
 * 
 * MovementAnimationSync - 移动与跳跃动画同步工具类
 * 从SlimeMovementDemo中提取的成功动画协调模式，用于生产环境
 */

import * as Phaser from 'phaser';

// 动画同步配置接口
export interface AnimationSyncConfig {
  jumpDuration: number;      // 跳跃持续时间 (默认500ms)
  spriteScale: number;       // 精灵缩放比例 (默认1.3)
  jumpHeightMultiplier: number; // 跳跃高度倍数 (默认1.2)
  shadowOffset: number;      // 阴影偏移 (默认8px)
  enableShadow: boolean;     // 是否启用阴影效果
  enableScaleEffect: boolean; // 是否启用缩放效果
}

// 默认配置
export const DEFAULT_SYNC_CONFIG: AnimationSyncConfig = {
  jumpDuration: 300, // 🔧 优化：减少到300ms，配合0.5s间隔和16fps动画
  spriteScale: 1.3,
  jumpHeightMultiplier: 1.2,
  shadowOffset: 8,
  enableShadow: true,
  enableScaleEffect: true
};

// 移动状态管理
export interface MovementState {
  isMoving: boolean;
  currentTileX: number;
  currentTileY: number;
  onComplete?: () => void;
}

// 移动动画同步器
export class MovementAnimationSync {
  private scene: Phaser.Scene;
  private config: AnimationSyncConfig;
  private movementStates: Map<string, MovementState> = new Map();

  constructor(scene: Phaser.Scene, config?: Partial<AnimationSyncConfig>) {
    this.scene = scene;
    this.config = { ...DEFAULT_SYNC_CONFIG, ...config };
  }

  /**
   * 执行同步移动动画 - 增强版
   * 基于Demo修复：立即播放walk动画，确保动画与位置移动完全同步
   */
  performSynchronizedMove(
    sprite: Phaser.GameObjects.Sprite,
    entityId: string,
    targetTileX: number,
    targetTileY: number,
    tileSize: number,
    walkAnimationKey: string,
    idleAnimationKey: string,
    onComplete?: () => void
  ): boolean {
    const state = this.getOrCreateMovementState(entityId);
    
    // 🔧 修复：现在Engine有延迟机制，理论上不应该有重叠，但保留警告
    if (state.isMoving) {
      console.warn(`⚠️ ${entityId} 精灵仍在移动中，这不应该发生（Engine应该有延迟）`);
      // 继续执行而不是阻塞，信任Engine的队列管理
    }

    state.isMoving = true;
    state.onComplete = onComplete;

    // 计算目标位置（瓦片中心）
    const targetX = targetTileX * tileSize + tileSize / 2;
    const targetY = targetTileY * tileSize + tileSize / 2;

    console.log(`🎯 ${entityId} 开始同步移动: (${state.currentTileX}, ${state.currentTileY}) → (${targetTileX}, ${targetTileY})`);

    // 🔧 关键修复：立即播放walk动画，与位置移动同步
    sprite.play(walkAnimationKey);
    console.log(`🚶 ${entityId} 立即播放walk动画: ${walkAnimationKey}`);

    // 🔧 优化：mov_3帧延迟 - 3帧@16fps = 187.5ms后开始精灵移动
    const frameDelay = (3 / 16) * 1000; // 3帧延迟转换为毫秒
    this.scene.time.delayedCall(frameDelay, () => {
      // 1. 主要位置移动tween
      this.scene.tweens.add({
        targets: sprite,
        x: targetX,
        y: targetY,
        duration: this.config.jumpDuration,
        ease: 'Power2',
      onStart: () => {
        // tween开始时确认动画播放状态
        console.log(`🏃 ${entityId} tween开始，动画同步确认`);
      },
      onComplete: () => {
        // 移动完成，更新状态
        state.currentTileX = targetTileX;
        state.currentTileY = targetTileY;
        state.isMoving = false;
        
        // 立即切换到idle动画
        sprite.play(idleAnimationKey);
        console.log(`🛑 ${entityId} 移动完成，切换到idle动画: ${idleAnimationKey}`);
        
        // 执行回调
        if (state.onComplete) {
          state.onComplete();
        }
        
        console.log(`✅ ${entityId} 移动完成: (${targetTileX}, ${targetTileY})`);
      }
    });

      // 2. 跳跃缩放效果
      if (this.config.enableScaleEffect) {
        this.addJumpScaleEffect(sprite);
      }

      // 3. 阴影效果（可通过全局开关禁用调试）
      const disableShadow = typeof window !== 'undefined' && (window as any).__DISABLE_SHADOW === true;
      if (this.config.enableShadow && !disableShadow) {
        console.log(`🌑 Shadow: create (sprite) → target=(${targetX}, ${targetY})`);
        this.addShadowEffect(sprite, targetX, targetY);
      }
    }); // 结束delayedCall

    return true;
  }

  /**
   * 容器移动动画同步 - 用于复杂的Agent容器（包含HP条等）
   * 基于Demo中修复的容器移动逻辑
   */
  performContainerMove(
    container: Phaser.GameObjects.Container,
    sprite: Phaser.GameObjects.Sprite,
    entityId: string,
    targetTileX: number,
    targetTileY: number,
    tileSize: number,
    walkAnimationKey: string,
    idleAnimationKey: string,
    onComplete?: () => void
  ): boolean {
    const state = this.getOrCreateMovementState(entityId);
    
    // 🔧 修复：现在Engine有延迟机制，理论上不应该有重叠，但保留警告
    if (state.isMoving) {
      console.warn(`⚠️ ${entityId} 容器仍在移动中，这不应该发生（Engine应该有延迟）`);
      // 继续执行而不是阻塞，信任Engine的队列管理
    }

    state.isMoving = true;
    state.onComplete = onComplete;

    // 计算目标位置（瓦片中心）
    const targetX = targetTileX * tileSize + tileSize / 2;
    const targetY = targetTileY * tileSize + tileSize / 2;

    console.log(`🎯 ${entityId} 容器开始移动: (${state.currentTileX}, ${state.currentTileY}) → (${targetTileX}, ${targetTileY})`);

    // 🔧 关键：立即播放walk动画
    sprite.play(walkAnimationKey);
    console.log(`🚶 ${entityId} 立即播放walk动画: ${walkAnimationKey}`);

    // 🔧 优化：mov_3帧延迟 - 3帧@16fps = 187.5ms后开始容器移动
    const frameDelay = (3 / 16) * 1000; // 3帧延迟转换为毫秒
    this.scene.time.delayedCall(frameDelay, () => {
      // 移动整个容器
      this.scene.tweens.add({
        targets: container,
        x: targetX,
        y: targetY,
        duration: this.config.jumpDuration, // 🔧 修复：使用统一的timing配置
        ease: 'Power2',
      onStart: () => {
        console.log(`🏃 ${entityId} 容器移动开始`);
      },
      onComplete: () => {
        // 移动完成，更新状态
        state.currentTileX = targetTileX;
        state.currentTileY = targetTileY;
        state.isMoving = false;
        
        // 立即切换到idle动画（容器/精灵可能已被淘汰流程移除，需判空）
        try {
          if (sprite && (sprite as any).play) {
            sprite.play(idleAnimationKey);
            console.log(`🛑 ${entityId} 容器移动完成，切换到idle动画`);
          }
        } catch {}
        
        // 执行回调
        if (state.onComplete) {
          state.onComplete();
        }
        
        console.log(`✅ ${entityId} 容器移动完成: (${targetTileX}, ${targetTileY})`);
      }
    });
    }); // 结束delayedCall

    return true;
  }

  /**
   * 添加跳跃缩放效果
   * 来自SlimeMovementDemo的成功实现
   */
  private addJumpScaleEffect(sprite: Phaser.GameObjects.Sprite): void {
    const originalScale = this.config.spriteScale;
    
    this.scene.tweens.add({
      targets: sprite,
      scaleY: originalScale * this.config.jumpHeightMultiplier, // 跳起时纵向拉伸
      duration: this.config.jumpDuration / 2,
      ease: 'Sine.easeOut',
      yoyo: true,
      onYoyo: () => {
        // 落地时稍微压扁
        sprite.setScale(originalScale * 1.05, originalScale * 0.95);
      },
      onComplete: () => {
        // 恢复正常比例
        sprite.setScale(originalScale);
      }
    });
  }

  /**
   * 添加阴影跟随效果
   * 来自SlimeMovementDemo的成功实现
   */
  private addShadowEffect(sprite: Phaser.GameObjects.Sprite, targetX: number, targetY: number): void {
    // 计算世界坐标：容器位置 + 子精灵本地偏移
    const parent = (sprite as any).parentContainer as Phaser.GameObjects.Container | undefined;
    const baseX = parent ? parent.x : 0;
    const baseY = parent ? parent.y : 0;
    const shadowX = baseX + sprite.x;
    const shadowY = baseY + sprite.y + this.config.shadowOffset;
    
    // 创建临时阴影
    const shadow = this.scene.add.ellipse(shadowX, shadowY, 16, 6, 0x000000, 0.2);
    shadow.setDepth(5); // 阴影在精灵下方
    
    this.scene.tweens.add({
      targets: shadow,
      x: targetX,
      y: targetY + this.config.shadowOffset,
      scaleX: 0.7,
      scaleY: 0.4,
      alpha: 0.1,
      duration: this.config.jumpDuration / 2,
      ease: 'Sine.easeOut',
      yoyo: true,
      onComplete: () => {
        shadow.destroy(); // 清理临时阴影
      }
    });
  }

  /**
   * 获取或创建移动状态
   */
  private getOrCreateMovementState(entityId: string): MovementState {
    let state = this.movementStates.get(entityId);
    if (!state) {
      state = {
        isMoving: false,
        currentTileX: 0,
        currentTileY: 0
      };
      this.movementStates.set(entityId, state);
    }
    return state;
  }

  /**
   * 更新实体的当前瓦片位置
   */
  updateCurrentPosition(entityId: string, tileX: number, tileY: number): void {
    const state = this.getOrCreateMovementState(entityId);
    state.currentTileX = tileX;
    state.currentTileY = tileY;
  }

  /**
   * 检查实体是否正在移动
   */
  isMoving(entityId: string): boolean {
    const state = this.movementStates.get(entityId);
    return state?.isMoving ?? false;
  }

  /**
   * 获取实体当前瓦片位置
   */
  getCurrentPosition(entityId: string): { x: number; y: number } {
    const state = this.movementStates.get(entityId);
    return {
      x: state?.currentTileX ?? 0,
      y: state?.currentTileY ?? 0
    };
  }

  /**
   * 清理实体状态（当实体被移除时调用）
   */
  cleanupEntity(entityId: string): void {
    this.movementStates.delete(entityId);
    console.log(`🗑️ 清理移动状态: ${entityId}`);
  }

  /**
   * 停止实体的所有移动动画
   */
  stopMovement(entityId: string): void {
    const state = this.movementStates.get(entityId);
    if (state) {
      state.isMoving = false;
      // 这里可以添加停止tween的逻辑，如果需要的话
    }
  }

  /**
   * 更新配置
   */
  updateConfig(newConfig: Partial<AnimationSyncConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * 获取当前配置
   */
  getConfig(): AnimationSyncConfig {
    return { ...this.config };
  }
}
